<template>
  <div class="pg-subj">
    <div class="pg-subj__head">
      <div class="p-field pg-subj__subtype">
        <label class="p-field__label">Practice Subtype</label>
        <CustomSelect
          id="subjectiveSubtypeSelect"
          :options="subjectiveSubtypeOptions"
          v-model="sectionData.content.practiceSubtype"
          @update:model-value="onSubjectiveSubtypeChange"
        />
      </div>
      <div class="p-field pg-subj__practice">
        <label class="p-field__label">Practice Text</label>
        <input
          v-model="sectionData.content.practiceText"
          type="text"
          class="p-input"
          placeholder="질문/지문을 입력하세요"
        />
      </div>
    </div>

    <!-- Short Answer -->
    <div v-if="sectionData.content.practiceSubtype === 'short'" class="pg-subj__options">
      <div
        v-for="(option, optionIdx) in sectionData.content.options"
        :key="optionIdx"
        class="pg-subj__item"
      >
        <div class="pg-subj__item-head">
          <span class="pg-subj__index">Option {{ optionIdx + 1 }}</span>
          <button
            v-if="sectionData.content.options.length > 1"
            type="button"
            class="pg-subj__icon-btn pg-subj__icon-btn--danger"
            title="Option 제거"
            @click.prevent="removeSubjectiveOption(optionIdx)"
          >
            <i class="material-icons-round">delete</i>
          </button>
        </div>

        <div class="pg-subj__pairs">
          <div
            v-for="(text, pairIdx) in option.option_text"
            :key="pairIdx"
            class="pg-subj__pair"
          >
            <div class="pg-subj__pair-fields">
              <div class="p-field">
                <label class="p-field__label">Option Text {{ pairIdx + 1 }}</label>
                <input
                  v-model="option.option_text[pairIdx]"
                  type="text"
                  class="p-input"
                  placeholder="보기 텍스트"
                />
              </div>
            </div>
            <div class="pg-subj__pair-image">
              <label class="p-field__label">Option Image</label>
              <image-upload
                :default-image="option.option_image[pairIdx] || require('@/assets/img/no_image.png')"
                :unique-id="'subjective-short-image-' + optionIdx + '-' + pairIdx"
                v-model="option.option_image[pairIdx]"
              />
            </div>
            <button
              v-if="option.option_text.length > 1"
              type="button"
              class="pg-subj__icon-btn pg-subj__icon-btn--danger pg-subj__pair-remove"
              title="쌍 제거"
              @click.prevent="removeTextImagePair(optionIdx, pairIdx)"
            >
              <i class="material-icons-round">close</i>
            </button>
          </div>

          <div class="pg-subj__pair-add">
            <ABtn kind="ghost" size="sm" icon="add" @click.prevent="addTextImagePair(optionIdx)">
              Text/Image 쌍 추가
            </ABtn>
          </div>
        </div>

        <div class="p-form-grid p-form-grid--2 pg-subj__item-footer">
          <div class="p-field">
            <label class="p-field__label">Hint</label>
            <input
              v-model="option.hint"
              type="text"
              class="p-input"
              placeholder="힌트"
            />
          </div>
          <div class="p-field">
            <label class="p-field__label">Answer</label>
            <input
              v-model="option.answer"
              type="text"
              class="p-input"
              placeholder="정답"
            />
          </div>
        </div>
      </div>

      <div class="pg-subj__actions">
        <ABtn kind="yellow" icon="add" @click.prevent="addSubjectiveOption">Option 추가</ABtn>
      </div>
    </div>

    <!-- Long Answer -->
    <div v-else class="pg-subj__long">
      <div class="p-field">
        <label class="p-field__label">Question Image</label>
        <image-upload
          :default-image="sectionData.content.practiceImage || require('@/assets/img/no_image.png')"
          unique-id="long-practice-image"
          v-model="sectionData.content.practiceImage"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineModel } from "vue";
import ImageUpload from "@/components/ImageUpload.vue";
import CustomSelect from "@/components/CustomSelect.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";

const sectionData = defineModel({ type: Object, required: true });

const subjectiveSubtypeOptions = [
  { value: "short", label: "Short Answer" },
  { value: "long", label: "Long Answer" },
];

const addSubjectiveOption = () => {
  if (!sectionData.value.content.options) {
    sectionData.value.content.options = [];
  }
  sectionData.value.content.options.push({
    option_text: [""],
    option_image: [null],
    hint: "",
    answer: "",
  });
};

const addTextImagePair = (optionIdx) => {
  sectionData.value.content.options[optionIdx].option_text.push("");
  sectionData.value.content.options[optionIdx].option_image.push(null);
};

const removeTextImagePair = (optionIdx, pairIdx) => {
  const option = sectionData.value.content.options[optionIdx];
  if (option.option_text.length > 1) {
    option.option_text.splice(pairIdx, 1);
    option.option_image.splice(pairIdx, 1);
  }
};

const onSubjectiveSubtypeChange = (newValue) => {
  if (newValue === "short") {
    sectionData.value.content.options = [
      { option_text: [""], option_image: [null], hint: "", answer: "" },
    ];
  } else if (newValue === "long") {
    delete sectionData.value.content.options;
    sectionData.value.content.practiceImage = "";
  }
};

const removeSubjectiveOption = (optionIdx) => {
  sectionData.value.content.options.splice(optionIdx, 1);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-subj {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__head {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 12px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__pairs {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__pair {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 200px;
    gap: 12px;
    align-items: start;
    padding: 10px 12px;
    background: $p-panel-alt;
    border-radius: $p-radius-sm;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__pair-fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__pair-image {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__pair-remove {
    position: absolute;
    top: 6px;
    right: 6px;
  }

  &__pair-add {
    display: flex;
    justify-content: flex-start;
  }

  &__item-footer {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed $p-line;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }

  &__long {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    max-width: 320px;
  }
}
</style>
